import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import { Link, Navigate } from 'react-router-dom';
import { Users, BarChart3, Settings, Calendar, Activity, Terminal, CalendarPlus, Monitor, ShieldCheck, Sparkles, Layers, HeartPulse } from 'lucide-react';

export default function Home() {
  const { user } = useAuth();
  const { setModuleTitle, isMobile } = useAppContext();

  useEffect(() => {
    setModuleTitle('Portal Principal - Módulos');
  }, [setModuleTitle]);

  if (user?.user_type === 'affiliate') {
    return <Navigate to="/affiliate/agendamiento" replace />;
  }

  if (user?.user_type === 'radiologo') {
    return <Navigate to="/radiologo/agenda" replace />;
  }

  const modules = [
    {
      title: 'Agendamiento y Atención al Paciente',
      description: 'Gestión de citas y programación de estudios.',
      icon: <CalendarPlus className="w-8 h-8 text-indigo-600" />,
      path: '/agendamiento',
      roles: ['admin', 'agendamiento', 'patient_care'],
    },
    {
      title: 'Recepción',
      description: 'Gestión de citas del día, pacientes y recepción.',
      icon: <Calendar className="w-8 h-8 text-blue-500" />,
      path: '/finanzas/recepcion',
      roles: ['admin', 'recepcion', 'patient_care'],
    },
    {
      title: 'Validador de Documentos',
      description: 'Firma digital de documentos con QR, certificación COFEPRIS y verificación pública.',
      icon: <ShieldCheck className="w-8 h-8 text-emerald-600" />,
      path: '/validador',
      roles: ['admin', 'medico', 'recepcion', 'patient_care', 'hr', 'dev'],
    },

    {
      title: 'Operaciones Médicas',
      description: 'Agenda técnica por modalidades, revisión Eden PACS y pantallas de sala.',
      icon: <Activity className="w-8 h-8 text-amber-500" />,
      path: '/operaciones',
      roles: ['admin', 'operaciones', 'patient_care', 'tecnico', 'medico', 'dev'],
    },
    {
      title: 'Sala de Estudios',
      description: 'Panel de técnicos para recepción, checklist y conclusión de estudios.',
      icon: <Monitor className="w-8 h-8 text-teal-600" />,
      path: '/operaciones?tab=salas',
      roles: ['admin', 'medico', 'tecnico', 'patient_care'],
    },
    {
      title: 'Recursos Humanos',
      description: 'Gestión de empleados, control de asistencias, horarios y vacaciones.',
      icon: <Users className="w-8 h-8 text-emerald-500" />,
      path: '/hr',
      roles: ['admin', 'hr'],
    },
    {
      title: 'Developer Tools',
      description: 'Herramientas de diagnóstico del sistema y carga JSON.',
      icon: <Terminal className="w-8 h-8 text-indigo-500" />,
      path: '/developer',
      roles: ['admin', 'dev'],
    },
    {
      title: 'CRM de Médicos',
      description: 'Gestión de médicos referentes, prospección y visitas.',
      icon: <Users className="w-8 h-8 text-blue-600" />,
      path: '/crm',
      roles: ['admin', 'crm', 'canvaseador', 'closer'],
    },
    {
      title: 'Marketing Masivo',
      description: 'Campañas de email marketing, segmentación y métricas de conversión.',
      icon: <BarChart3 className="w-8 h-8 text-purple-600" />,
      path: '/marketing',
      roles: ['admin', 'marketing'],
    },
    {
      title: 'Tickets Internos y Tareas',
      description: 'Gestión corporativa de tareas, peticiones, deadlines y seguimiento.',
      icon: <Layers className="w-8 h-8 text-indigo-600" />,
      path: '/tickets',
      roles: ['admin', 'operaciones', 'hr', 'recepcion', 'finance', 'patient_care', 'dev', 'medico', 'tecnico', 'agendamiento', 'crm', 'marketing', 'empleado'],
    },
    {
      title: 'Portal Médico Radiólogo',
      description: 'Visualización y gestión clínica de estudios coronarios y agenda de radiología.',
      icon: <HeartPulse className="w-8 h-8 text-rose-600" />,
      path: '/radiologo/agenda',
      roles: ['admin', 'radiologo', 'radiologo.agenda', 'medico'],
    },
    {
      title: 'Affiliate Marketing Platform',
      description: 'Portal de agendamiento y comisiones para socios y afiliados de marketing.',
      icon: <Sparkles className="w-5 h-5 text-indigo-600" />,
      path: '/affiliate/agendamiento',
      roles: ['admin', 'affiliate', 'affiliate.agendamiento', 'marketing'],
    },
    {
      title: 'System Check',
      description: 'Monitor de procesos, webhooks y tráfico del sistema.',
      icon: <Settings className="w-8 h-8 text-red-500" />,
      path: '/system/processes',
      roles: ['admin', 'dev'],
    }
  ];

  const userRoles = Array.isArray(user?.roles) ? user.roles : [];
  const accessibleModules = modules.filter(m => 
    Array.isArray(m.roles) && m.roles.some(r => userRoles.includes(r))
  );

  if (isMobile) {
    return (
      <div className="flex-1 overflow-y-auto bg-slate-50 p-4">
        <h2 className="text-base font-black text-slate-800 mb-4">Módulos Disponibles</h2>
        {accessibleModules.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 text-xs text-yellow-700 font-bold">
            No tienes permisos asignados a ningún módulo. Contacta al administrador.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {accessibleModules.map((mod, idx) => (
              <Link key={idx} to={mod.path} className="block active:scale-[0.99] transition-transform">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center gap-4">
                  <div className="p-2 bg-slate-50 rounded-xl border border-slate-100 shrink-0">
                    {mod.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-black text-slate-800">{mod.title}</h3>
                    <p className="text-xs font-bold text-slate-500 mt-0.5 leading-tight">{mod.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-50 px-8 py-8">
      <h2 className="text-2xl font-black text-slate-800 mb-6">Módulos Disponibles</h2>
      
      {accessibleModules.length === 0 ? (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-xl shadow-sm">
          <p className="text-sm font-bold text-yellow-700">
            No tienes permisos asignados a ningún módulo. Contacta al administrador.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {accessibleModules.map((mod, idx) => (
            <Link key={idx} to={mod.path} className="block group">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-lg hover:border-indigo-300 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 group-hover:bg-indigo-50/50 transition-colors">
                    {mod.icon}
                  </div>
                  <span className="text-xs font-black text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    Entrar →
                  </span>
                </div>
                <h3 className="text-lg font-black text-slate-800 mb-2">{mod.title}</h3>
                <p className="text-sm font-semibold text-slate-500 leading-relaxed">{mod.description}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
